import { Component, OnInit, TemplateRef, ViewChild } from '@angular/core';

import {
  BankProduct,
  ContractPublicService,
  CustomerAreaDetail,
  CustomerAreaDetailService,
  InsurancePackage,
  KreditInsuranceCancellationReminder,
  KreditInsuranceInfo,
  KreditInsuranceService,
  Offer,
  UploadTemplate,
} from '../../../openapi-ts';
import { translateLoanPurpose } from '../../utils/translate';
import { LoginService } from '../../share/service/login.service';
import { closingtrackUrl } from '../../utils/url';
import { currencyFormatter } from 'src/app/utils/currencyFormatter';
import { DialogService } from '../../share/service/dialog.service';
import { DialogRef } from '@angular/cdk/dialog';
import { ResultpageService } from '../../share/service/resultpage.service';

type AttachmentDocument = {
  filename?: string;
  contentBase64?: string;
};

@Component({
  selector: 'app-activity-page',
  templateUrl: './activity-page.component.html',
  styleUrl: './activity-page.component.scss',
})
export class ActivityPageComponent implements OnInit {
  @ViewChild('kreditInsuranceDialogContent')
  kreditInsuranceDialogContent!: TemplateRef<any>;
  @ViewChild('kreditInsuranceDialogActions')
  kreditInsuranceDialogActions!: TemplateRef<any>;

  customerAreaDetails$ = this.customerAreaDetailService.customerAreaDetail(
    this.getToken(),
  );
  public cardData: CustomerAreaDetail;
  public activated: number = 0;
  public showProgress: boolean = false;
  public kreditInsuranceDetails: InsurancePackage = null;
  private dialogRef: DialogRef<string> = null;
  public isKreditInsurance: boolean = false;
  public activeApplicant: number = 0;
  public isContinueWithout: boolean = false;
  public choosenPackageId: string = null;
  public isKreditInsuranceStateCancelled: boolean = false;
  public isDeductionStateCancelled: boolean = false;

  constructor(
    private customerAreaDetailService: CustomerAreaDetailService,
    public contractDownloadService: ContractPublicService,
    public loginService: LoginService,
    private dialogService: DialogService,
    private kreditInsuranceService: KreditInsuranceService,
    private resultpageService: ResultpageService,
  ) {}

  ngOnInit() {
    this.resultpageService.checkLogin().subscribe({
      next: () => {
        this.loadCustomerAreaDetails();
      },
    });
  }

  private loadCustomerAreaDetails(): void {
    // Create/recreate the observable with fresh data
    this.customerAreaDetails$ =
      this.customerAreaDetailService.customerAreaDetail(this.getToken());

    // Subscribe and process the data
    this.customerAreaDetails$.subscribe((data) => {
      this.cardData = data;
      this.setProgress(data.offer.contractState);
      this.updateKreditInsuranceDetails();
      // Show insurance info if kreditInsurance exists OR if a package was chosen
      this.isKreditInsurance = this.cardData.offer?.kreditInsurance != null;
    });
  }

  getToken() {
    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);
    return urlParams.get('activityToken') ?? '';
  }

  getReminderMailAttachment(filename: string) {
    const kreditInsuranceState = this.getKreditInsuranceState();
    const isKreditInsuranceStateSubmitted =
      kreditInsuranceState ===
      KreditInsuranceInfo.KreditInsuranceStateEnum.Submitted;

    const applicant = this.getActiveApplicantEnum();

    // Choose endpoint based on insurance state
    const attachmentCall$ = isKreditInsuranceStateSubmitted
      ? this.contractDownloadService.getThankYouAttachments(
          this.cardData.offer.contractToken,
          applicant,
        )
      : this.contractDownloadService.getReminderAttachments(
          this.cardData.offer.contractToken,
          applicant,
        );

    attachmentCall$.subscribe({
      next: async (response: any) => {
        try {
          const jsonString = await response.text();
          const documents: AttachmentDocument[] = JSON.parse(jsonString);
          const normalizeFilename = (name: string): string => {
            return name.toLowerCase().replace(/\.pdf$/i, '');
          };
          const normalizedSearchFilename = normalizeFilename(filename);

          // Filter and download only the document matching the filename
          const matchingDocument = documents.find((document) => {
            const normalizedDocFilename = normalizeFilename(
              document.filename || '',
            );
            return normalizedDocFilename === normalizedSearchFilename;
          });

          if (matchingDocument && matchingDocument.contentBase64) {
            // Convert base64 to blob and download directly
            this.downloadBase64Document(
              matchingDocument.contentBase64,
              matchingDocument.filename,
            );
          } else {
            console.warn(`Document with filename "${filename}" not found`);
          }
        } catch (error) {
          console.error('Error:', error);
        }
      },
    });
  }
  getContractDownload() {
    const contractData = this.contractDownloadService.contractDownload(
      this.cardData.offer.contractToken,
    );
    contractData.subscribe((data) => {
      this.downloadAsPdf(data, 'Kreditvertrag');
    });
  }

  downloadBase64Document(base64Data: string, fileName: string) {
    // Convert base64 to bytes
    const binaryString = atob(base64Data);
    const bytes = Uint8Array.from(binaryString, (char) => char.charCodeAt(0));
    this.downloadAsPdf(bytes, fileName);
  }

  private downloadAsPdf(
    data: Uint8Array | ArrayBuffer | ArrayBufferView | Blob | string,
    fileName: string,
  ) {
    // Create PDF blob and download
    const blob = new Blob([data], { type: 'application/pdf' });

    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName; // The file name for the downloaded PDF
    document.body.appendChild(a);
    a.click();
    // Clean up
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  }

  getCardData(): {
    id: string;
    purpose: { name: string; value: string };
    offerResponse: Offer;
    contractToken: string;
    bankProduct: BankProduct;
    twoApplicants: boolean;
    templates1: UploadTemplate[];
    templates2: UploadTemplate[];
  } {
    return {
      id: this.cardData?.offer?.id,
      purpose: {
        name: translateLoanPurpose(
          this.cardData?.offer?.bankApplication?.loan?.purpose,
        ),
        value: this.cardData?.offer?.bankApplication?.loan?.purpose,
      },
      offerResponse: this.cardData?.offer?.offerResponse,
      contractToken: this.cardData?.offer?.contractToken,
      bankProduct: this.cardData?.offer?.bankProduct,
      twoApplicants:
        this.cardData?.offer?.bankApplication?.applicants?.twoApplicants,
      templates1: this.cardData?.offer?.documentUpload?.applicant1?.templates,
      templates2: this.cardData?.offer?.documentUpload?.applicant2?.templates,
    };
  }

  switchDetails(element): void {
    this.activated = element;
  }

  setProgress(contractState) {
    if (
      [
        Offer.ContractStateEnum.IdentFinished,
        Offer.ContractStateEnum.ClosingFinished,
        Offer.ContractStateEnum.DocumentsChecked,
        Offer.ContractStateEnum.DocumentsChecked2Nd,
        Offer.ContractStateEnum.DocumentsMissing,
        Offer.ContractStateEnum.DocumentsMissing2Nd,
        Offer.ContractStateEnum.DocumentsUploaded,
        Offer.ContractStateEnum.DocumentsUploaded2Nd,
        Offer.ContractStateEnum.Closed,
        Offer.ContractStateEnum.PreliminaryAccepted,
      ].includes(contractState)
    ) {
      this.showProgress = true;
    }
  }

  onClosingTrack(token: string, loanAmount: number): void {
    window.location.href = closingtrackUrl(
      token,
      loanAmount,
      this.cardData.offer.contractStateFlow,
    );
  }

  private updateKreditInsuranceDetails() {
    if (this.cardData?.offer?.kreditInsurance) {
      const insuranceInfo = this.getCurrentApplicantInsuranceInfo();

      this.choosenPackageId = insuranceInfo?.chosenPackageId || null;
      this.isContinueWithout = insuranceInfo?.continueWithout || false;

      const kreditInsuranceState = this.getKreditInsuranceState();
      this.isKreditInsuranceStateCancelled =
        kreditInsuranceState ===
        KreditInsuranceInfo.KreditInsuranceStateEnum.Cancelled;

      const deductionState = insuranceInfo?.deductionState || null;
      this.isDeductionStateCancelled =
        deductionState === KreditInsuranceInfo.DeductionStateEnum.Canceled;

      if (this.choosenPackageId) {
        this.kreditInsuranceDetails =
          insuranceInfo?.possibleInsurances?.[0]?.insurancePackages?.find(
            (item) => item.id === this.choosenPackageId,
          ) || null;
      } else {
        // reset kreditInsuranceDetail after package cancellation.
        this.kreditInsuranceDetails = null;
      }
    } else {
      this.kreditInsuranceDetails = null;
    }
  }

  private getActiveApplicantEnum(): 'ONE' | 'TWO' {
    return this.activeApplicant === 0
      ? KreditInsuranceCancellationReminder.ApplicantEnum.One
      : KreditInsuranceCancellationReminder.ApplicantEnum.Two;
  }

  private getCurrentApplicantKey(): 'applicantOne' | 'applicantTwo' {
    return this.activeApplicant === 0 ? 'applicantOne' : 'applicantTwo';
  }

  private getCurrentApplicantInsuranceInfo(): KreditInsuranceInfo | null {
    const currentApplicant = this.getCurrentApplicantKey();
    return this.cardData?.offer?.kreditInsurance?.[currentApplicant] || null;
  }

  private getKreditInsuranceState(): KreditInsuranceInfo.KreditInsuranceStateEnum | null {
    const insuranceInfo = this.getCurrentApplicantInsuranceInfo();
    return insuranceInfo?.kreditInsuranceState || null;
  }

  getKreditInsuranceClosingtrackUrl(): void {
    const token = this.getToken();
    const applicant = this.getActiveApplicantEnum();

    this.customerAreaDetailService
      .resetKreditInsuranceForReentry(token, applicant)
      .subscribe({
        next: (redirectUrl: string) => {
          const trimmedUrl = redirectUrl?.trim();
          if (trimmedUrl) {
            window.location.href = trimmedUrl;
          }
        },
        error: (error) => {
          console.error('Error resetting insurance for reentry:', error);
        },
      });
  }

  openKreditInsuranceDialog(): void {
    this.dialogRef = this.dialogService.open(
      'Kreditschutz stornieren',
      this.kreditInsuranceDialogContent,
      this.kreditInsuranceDialogActions,
      {
        panelClass: 'kredit-insurance-dialog-panel',
      },
    );
    this.dialogRef.closed.subscribe(() => {
      this.dialogRef = null;
    });
  }

  confirmKreditInsuranceCancellation(): void {
    const token = this.getToken();
    const applicant = this.getActiveApplicantEnum();

    // Make the POST request to cancel the insurance
    this.customerAreaDetailService
      .updateKreditInsuranceToCancelled(token, applicant)
      .subscribe({
        next: () => {
          if (this.dialogRef) {
            this.dialogRef.close('cancelled');
          }
          // Refresh the data for customerAreaDetails.
          this.loadCustomerAreaDetails();
        },
        error: (error) => {
          console.error('Error cancelling insurance:', error);
        },
      });
  }

  protected readonly Object = Object;
  protected readonly Offer = Offer;

  currencyFormatter = currencyFormatter;

  handleApplicantChange(applicant: number): void {
    this.activeApplicant = applicant;
    this.loadCustomerAreaDetails();
  }
}
